import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Popover,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";

const DivisionManagementTable = () => {
  // ✅ RESPONSIVE HOOKS
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [filter, setFilter] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [nameError, setNameError] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [categoryToDelete, setcategoryToDelete] = useState(null);
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [categoryForm, setCategoryForm] = useState({
    name: "",
    created_by: localStorage.getItem("user_id"),
  });

  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [multiCategories, setMultiCategories] = useState([{ name: "" }]);

  const fetchCategories = async (
    reset = false,
    pageNo = 1,
    search = filter
  ) => {
    const data = await getCategories(pageNo, search);

    if (reset) {
      setCategories(data.results);
    } else {
      setCategories((prev) => [...prev, ...data.results]);
    }

    setHasMore(!!data.next);
    setPage(pageNo + 1);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchCategories(true, 1, filter);
    }, 500);

    return () => clearTimeout(handler);
  }, [filter]);

  const handleSave = async (nameParam) => {
    const createdBy = localStorage.getItem("user_id");
    const finalName = nameParam || categoryForm.name;

    if (!finalName.trim()) {
      setNameError("Category name is required");
      return;
    }
    setNameError("");

    if (editId) {
      const update_res = await updateCategory(editId, { name: finalName });
      setCategories(
        categories.map((category_data) =>
          category_data.id === editId
            ? { ...categories, ...update_res }
            : category_data
        )
      );
      toast.success("Category updated successfully");
    } else {
      const response = await createCategory({
        name: finalName,
        created_by: createdBy,
      });
      toast.success("Category added successfully");
      if (Array.isArray(response)) {
        setCategories([...response, ...categories]);
      } else {
        setCategories([...response, ...categories]);
      }
    }
    setCategoryForm({ name: "", created_by: createdBy });
    setEditId(null);
    setOpen(false);
  };

  const handleSubmitAll = async () => {
    const createdBy = localStorage.getItem("user_id");
    const validCategories = multiCategories
      .filter((c) => c.name.trim() !== "")
      .map((c) => ({
        name: c.name.trim(),
        created_by: createdBy,
      }));

    if (validCategories.length === 0) {
      setNameError("Please enter at least one category");
      return;
    }
    const response = await createCategory(validCategories);
    if (Array.isArray(response)) {
      setCategories([...response, ...categories]);
    } else {
      setCategories([...response, ...categories]);
    }

    setMultiCategories([{ name: "" }]);
    setOpen(false);
  };

  const handleEdit = (cat) => {
    setCategoryForm({ name: cat.name });
    setEditId(cat.id);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteCategory(id);
      setCategories((prev) => prev.filter((cat) => cat.id !== id));
      toast.success("Deleted successfully");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleAddField = () => {
    if (multiCategories.length >= 5) {
      toast.error("You can add maximum 5 categories");
      return;
    }
    setMultiCategories([...multiCategories, { name: "" }]);
  };

  const handleRemoveField = (index) => {
    setMultiCategories(multiCategories.filter((_, i) => i !== index));
  };

  const handleChange = (index, value) => {
    const newFields = [...multiCategories];
    newFields[index].name = value;
    setMultiCategories(newFields);
  };

 function formatOnlyDate(dateTimeString) {
  if (!dateTimeString) return "N/A";

  // Take only the date part (before T)
  const datePart = dateTimeString.split("T")[0];  // "2025-12-02"

  const [year, month, day] = datePart.split("-");

  return `${day}-${month}-${year}`;
}

  useEffect(() => {
    document.body.style.overflow = isMobile ? 'auto' : 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobile]);

  return (
    <Box>
      <Box
        display="flex"
        flexWrap="wrap"
        gap={2}
        alignItems="center"
        sx={{
          mt: -3,
          pt: -3,
          mb: 1,
        }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 200 }}>
          <TextField
            label="Search by category"
            variant="outlined"
            size="small"
            fullWidth
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{
              "& .MuiInputBase-root": {
                height: 30,
                fontSize: "0.7rem",
                borderRadius: "0",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.7rem",
              },
            }}
          />
        </Box>

        <Box sx={{ ml: "15px" }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: "30px",
              fontSize: "0.7rem",
              padding: "4px 10px",
              borderRadius: "0px",
            }}
            onClick={() => {
              setEditId(null);
              setCategoryForm({ name: "" });
              setMultiCategories([{ name: "" }]);
              setOpen(true);
            }}
          >
            Category
          </Button>
        </Box>
      </Box>

      <div id="project-table-print">
        <InfiniteScroll
          dataLength={categories.length}
          next={() => fetchCategories(false, page, filter)}
          hasMore={hasMore}
          // height={isMobile ? "75vh" : "80vh"}
          loader={
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                Loading...
              </Typography>
            </Box>
          }
          endMessage={
            <Typography
              sx={{
                textAlign: "center",
                py: 2,
                fontSize: { xs: "0.85rem", sm: "1rem" },
                color: "#999",
              }}
            >
              No more categories
            </Typography>
          }
        >
          {/* 🔥 CONDITIONAL RENDERING - Mobile vs Desktop */}
          {isMobile ? (
            /* 📱 MOBILE VIEW - Simple Single Line Card */
            <Box sx={{ bgcolor: "background.paper" }}>
              {categories.map((cat, index) => (
                <Box
                  key={cat.id || index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    p: 1.5,
                    bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                    borderBottom: "1px solid #e0e0e0",
                    "&:active": {
                      bgcolor: "#e3f2fd",
                    },
                  }}
                >
                  {/* Left: S.No + Name + Date */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* S.No aur Name ek line me */}
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: "#333",
                        mb: 0.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span style={{ color: "#666", fontSize: "0.75rem" }}>
                        {index + 1}
                      </span>{" "}
                      {cat.name}
                    </Typography>

                    {/* Date niche */}
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "#999",
                        ml: 1,
                      }}
                    >
                      {formatOnlyDate(cat.created_at)}
                    </Typography>
                  </Box>

                  {/* Right: Edit & Delete Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    {/* Edit Button */}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(cat);
                        setOpen(true);
                      }}
                      sx={{
                        bgcolor: "#e3f2fd",
                        borderRadius: "4px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: "#bbdefb",
                        },
                      }}
                    >
                      <EditIcon sx={{ fontSize: "0.95rem", color: "#1976d2" }} />
                    </IconButton>

                    {/* Delete Button */}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnchorEl(e.currentTarget);
                        setcategoryToDelete(cat.id);
                      }}
                      sx={{
                        bgcolor: "#ffebee",
                        borderRadius: "4px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: "#ffcdd2",
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: "0.95rem", color: "#d32f2f" }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            /* 💻 DESKTOP VIEW - Table Layout */
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: "88vh",
                overflowX: "auto",
                overflowY: "auto",
                width: "100%",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1000,
                  "& td, & th": {
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid #ccc",
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": {
                        fontWeight: "bold",
                        color: "#333",
                        borderBottom: "1px solid #999",
                        backgroundColor: "#f5f5f5",
                        fontSize: "0.75rem",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        backgroundColor: "#f5f5f5",
                        minWidth: 50,
                      }}
                    >
                      S.No.
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f5f5f5" }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f5f5f5" }}>
                      Date
                    </TableCell>
                    <TableCell
                      className="no-print"
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#f5f5f5",
                        zIndex: 3,
                        minWidth: 80,
                        padding: 0,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {categories.map((cat, index) => (
                    <TableRow
                      hover
                      key={cat.id || index}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#fafafa" : "#ffffff",
                        "& td": {
                          paddingRight: "13px !important",
                          paddingLeft: "13px !important",
                          padding: "0px",
                          fontSize: { xs: "0.65rem", sm: "0.75rem" },
                          whiteSpace: "nowrap",
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "black" : "inherit",
                          color: (theme) =>
                            theme.palette.mode === "dark" ? "white" : "inherit",
                        },
                        "&:hover": {
                          backgroundColor: "#e3f2fd !important",
                          "& td": {
                            backgroundColor: "#e3f2fd !important",
                          },
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: "inherit",
                          zIndex: 1,
                        }}
                      >
                        {index + 1}
                      </TableCell>
                      <TableCell>{cat.name}</TableCell>
                      <TableCell>{formatOnlyDate(cat.created_at)}</TableCell>
                      <TableCell
                        className="no-print"
                        sx={{
                          position: "sticky",
                          right: 0,
                          backgroundColor: "inherit",
                          zIndex: 1,
                          minWidth: 80,
                        }}
                      >
                        <IconButton
                          color="primary"
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          size="small"
                          onClick={() => {
                            handleEdit(cat);
                            setOpen(true);
                          }}
                        >
                          <EditIcon transform="scale(0.6)" />
                        </IconButton>
                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          color="error"
                          size="small"
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setcategoryToDelete(cat.id);
                          }}
                        >
                          <DeleteIcon transform="scale(0.6)" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </InfiniteScroll>
      </div>

      {/* Add/Edit Modal */}
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setEditId(null);
          setCategoryForm({ name: "" });
          setMultiCategories([{ name: "" }]);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Manage Categories</DialogTitle>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (editId) {
              handleSave();
            } else {
              handleSubmitAll();
            }
          }}
        >
          <DialogContent dividers>
            {editId ? (
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  label="Category Name"
                  fullWidth
                  value={categoryForm.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCategoryForm((prev) => ({
                      ...prev,
                      name: value,
                    }));
                    if (value.trim()) setNameError("");
                  }}
                  error={!!nameError}
                  helperText={nameError}
                  required
                />
              </Box>
            ) : (
              <>
                {multiCategories.map((cat, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 2,
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      label={`Category ${index + 1}`}
                      fullWidth
                      value={cat.name}
                      onChange={(e) => handleChange(index, e.target.value)}
                      required
                    />
                    <Box sx={{ width: 40, textAlign: "center" }}>
                      {multiCategories.length > 1 && (
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleRemoveField(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ))}
              </>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              justifyContent: "flex-start",
              mb: -5,
            }}
          >
            <Button
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={handleAddField}
              sx={{
                height: 35,
                fontSize: 14,
                padding: "2px 10px",
                minWidth: "auto",
                textTransform: "none",
                border: "none",
                display: "flex",
                backgroundColor: "white",
              }}
            >
              ADD ROW
            </Button>
          </DialogActions>

          <DialogActions>
            <Button
              color="error"
              onClick={() => {
                setOpen(false);
                setEditId(null);
                setCategoryForm({ name: "" });
                setMultiCategories([{ name: "" }]);
              }}
            >
              CLOSE
            </Button>

            {!editId && (
              <Button
                type="submit"
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontSize: "0.85rem",
                  padding: "4px 12px",
                  color: "rgba(25, 127, 230, 1)",
                }}
              >
                SAVE
              </Button>
            )}
            {editId && (
              <Button type="submit" variant="contained">
                Update
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* Confirmation Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setcategoryToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">Are you sure?</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button
              color="error"
              size="small"
              onClick={async () => {
                try {
                  await handleDelete(categoryToDelete);
                } finally {
                  setAnchorEl(null);
                  setcategoryToDelete(null);
                }
              }}
            >
              Yes
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setcategoryToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>

      <ToastContainer position="top-right" autoClose={3000} />
    </Box>
  );
};

export default DivisionManagementTable;